import React, { useState, useEffect } from "react";
import { Container, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import Swal from "sweetalert2";
import apiClient from "../../axiosInstance";
import TanstackTable from "../../components/Common/TanstackTable";
import ToastrNotification from "../../components/Common/Notification";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const UsersList = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Users List", link: "#" },
  ]);

  const [data, setData] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const result = await apiClient.get("/users/getAllUsers");
        const response = result.data.response;
        setData(response);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };

    fetchUsers();
  }, []);
  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Suspended" : "Active";
      const updateStatus = currentStatus === "Active" ? 2 : 1;
      setData((prevData) =>
        prevData.map((user) =>
          user.id === userId ? { ...user, user_status: newStatus } : user
        )
      );
      await apiClient.put(`/users/updateStatus/${userId}`, {
        status: updateStatus,
      });
      if (newStatus === "Active") {
        setToast({
          message: "User activated successfully",
          title: "User Status",
          type: "success",
        });
      } else {
        setToast({
          message: "User suspended successfully",
          title: "User Status",
          type: "error",
        });
      }
      setTimeout(() => {
        setToast(null);
      }, 3000);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const handleResetPassword = async (user) => {
    const { value: formValues } = await Swal.fire({
      title: `Reset Password: ${user.username}`,
      html:
        '<input id="newPassword" type="password" class="swal2-input" placeholder="New password" autocomplete="new-password">' +
        '<input id="confirmPassword" type="password" class="swal2-input" placeholder="Confirm password" autocomplete="new-password">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Reset Password",
      preConfirm: () => {
        const newPassword = document.getElementById("newPassword").value;
        const confirmPassword = document.getElementById("confirmPassword").value;

        if (!newPassword || !confirmPassword) {
          Swal.showValidationMessage("Both password fields are required");
          return false;
        }

        if (newPassword.length < 6) {
          Swal.showValidationMessage("Password must be at least 6 characters");
          return false;
        }

        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage("Passwords do not match");
          return false;
        }

        return { newPassword };
      },
    });

    if (!formValues?.newPassword) {
      return;
    }

    try {
      const response = await apiClient.put(`/users/resetPassword/${user.id}`, {
        newPassword: formValues.newPassword,
      });

      setToast({
        message: response?.data?.message || "Password reset successfully",
        title: "Reset Password",
        type: "success",
      });
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to reset password";

      setToast({
        message,
        title: "Reset Password",
        type: "error",
      });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const columns = [
    {
      id: "index",
      header: "No",
      cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "full_name",
      header: "Full Name",
      cell: ({ row }) => {
        const fullName = row.original.full_name;
        const photoName = row.original.profile_photo;
        const photoUrl = `https://daryeelinspection.com/images/users/${photoName}`;
        return (
          <div className="d-flex align-items-center gap-2">
            <img
              src={photoUrl}
              alt="Profile"
              className="img-thumbnail rounded-circle avatar-sm"
            />
            <span>{fullName}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "username",
      header: "Username",
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "gender",
      header: "Gender",
    },
    {
      accessorKey: "branch_name",
      header: "Branch",
    },
    {
      accessorKey: "role_name",
      header: "Role",
    },
    {
      accessorKey: "user_status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.user_status;
        const userId = row.original.id;
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              className={`badge bg-${
                status === "Active" ? "success" : "secondary"
              }`}
            ></div>
            <div className="form-check form-switch m-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={status === "Active"}
                onChange={() => handleStatusToggle(userId, status)}
              />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "last_login",
      header: "Last Login",
      cell: ({ getValue }) => {
        const date = getValue();
        return date ? (
          formatDistanceToNow(new Date(date), { addSuffix: true })
        ) : (
          <span className="text-muted">Never</span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Link
            to={`/users/${row.original.id}`}
            className="btn btn-sm btn-primary"
            title="Edit User"
          >
            <i className="ri-file-edit-line"></i>
          </Link>
          <button
            type="button"
            className="btn btn-sm btn-warning"
            title="Reset Password"
            onClick={() => handleResetPassword(row.original)}
          >
            <i className="ri-lock-password-line"></i>
          </button>
        </div>
      ),
    },
  ];
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Users List" breadcrumbItems={breadcrumbItems} />
        <Card>
          <CardBody>
            <div className="row">
              <div className="col-12 text-end mb-2">
                <Link to="/createUser">
                  <button className="btn btn-primary">Create User</button>
                </Link>
              </div>
            </div>
            <TanstackTable data={data} columns={columns} />
            {toast && (
              <ToastrNotification
                message={toast.message}
                title={toast.title}
                type={toast.type}
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default UsersList;
